'use client';

import React from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '../../primitives/dropdown-menu';

import type { ReactNode } from 'react';

/**
 * Schema-driven dropdown menu.
 *
 * Pass `items` and we render the menu. Keeps call sites declarative and
 * makes adding analytics, permission gating, and i18n easier in one place.
 */

export interface UzDropdownItem {
  type?: 'item';
  label: ReactNode;
  icon?: ReactNode;
  shortcut?: string;
  onSelect?: () => void;
  disabled?: boolean;
  danger?: boolean;
}

export interface UzDropdownSeparator {
  type: 'separator';
}

export interface UzDropdownLabel {
  type: 'label';
  label: ReactNode;
}

export type UzDropdownMenuItem = UzDropdownItem | UzDropdownSeparator | UzDropdownLabel;

export interface UzDropdownMenuProps {
  trigger: ReactNode;
  items: UzDropdownMenuItem[];
  align?: 'start' | 'center' | 'end';
  /** Extra classes on the menu surface, e.g. a max-height + scroll for long lists. */
  contentClassName?: string;
}

export const UzDropdownMenu = ({
  trigger,
  items,
  align = 'end',
  contentClassName,
}: UzDropdownMenuProps): React.JSX.Element => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
    {/* Stop click/pointer bubbling: the content is portaled but stays in the
        React tree, so without this an item click also fires an ancestor's
        onClick (e.g. a clickable table row navigating/opening edit). */}
    <DropdownMenuContent
      align={align}
      className={contentClassName}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {items.map((item, i) => {
        if (item.type === 'separator') return <DropdownMenuSeparator key={`sep-${i}`} />;
        if (item.type === 'label')
          return <DropdownMenuLabel key={`lbl-${i}`}>{item.label}</DropdownMenuLabel>;
        return (
          <DropdownMenuItem
            key={`item-${i}`}
            onSelect={item.onSelect}
            disabled={item.disabled}
            className={item.danger ? 'text-destructive focus:text-destructive' : undefined}
          >
            {item.icon && <span className="mr-2 inline-flex">{item.icon}</span>}
            {item.label}
            {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  </DropdownMenu>
);
